import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getStoredUser, setStoredUser, getToken, setToken, clearAuth } from '../utils/auth';
import * as authApi from '../api/auth.api';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getStoredUser());
  const [loading, setLoading] = useState(Boolean(getToken()));

  // On boot, if we have a token, refresh user from /auth/me.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!getToken()) { setLoading(false); return; }
      try {
        const u = await authApi.me();
        if (!cancelled) {
          setUser(u);
          setStoredUser(u);
        }
      } catch {
        if (!cancelled) {
          clearAuth();
          setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // Shared helper — when the backend returns { requiresOtp: true, ... }
  // we bubble that up instead of storing a token (there isn't one yet).
  // Callers must next invoke verifyLoginOtp(data.userId, otp) to complete.
  function finalizeAuth(data) {
    if (data?.requiresOtp) return data;
    setToken(data.token);
    setStoredUser(data.user);
    setUser(data.user);
    return data;
  }

  const signIn = useCallback(async (email, password) => {
    return finalizeAuth(await authApi.login(email, password));
  }, []);

  const signUp = useCallback(async (payload) => {
    const data = await authApi.register(payload);
    setToken(data.token);
    setStoredUser(data.user);
    setUser(data.user);
    return data.user;
  }, []);

  const sendOtp = useCallback(async (phone, opts) => {
    return authApi.sendOtp(phone, opts);
  }, []);

  const verifyOtp = useCallback(async (phone, otp, opts) => {
    const data = await authApi.verifyOtp(phone, otp, opts);
    setToken(data.token);
    setStoredUser(data.user);
    setUser(data.user);
    return data; // { user, token, isNewUser }
  }, []);

  // New USER flow — phone + OTP + password signup / phone + password login.
  const signUpUser = useCallback(async (payload) => {
    const data = await authApi.signUpUser(payload);
    setToken(data.token);
    setStoredUser(data.user);
    setUser(data.user);
    return data.user;
  }, []);

  const signInUser = useCallback(async (phone, password) => {
    return finalizeAuth(await authApi.signInUser(phone, password));
  }, []);

  const forgotPasswordOtp = useCallback(async (phone) => {
    return authApi.forgotPasswordOtp(phone);
  }, []);

  const resetPasswordOtp = useCallback(async (phone, otp, password) => {
    const data = await authApi.resetPasswordOtp(phone, otp, password);
    setToken(data.token);
    setStoredUser(data.user);
    setUser(data.user);
    return data.user;
  }, []);

  const signInSuperAdmin = useCallback(async (email, password, code) => {
    return finalizeAuth(await authApi.loginSuperAdmin(email, password, code));
  }, []);

  const signInPortal = useCallback(async (email, password) => {
    return finalizeAuth(await authApi.loginPortal(email, password));
  }, []);

  // MFA step 2 — final call that produces a JWT.
  const verifyLoginOtp = useCallback(async (userId, otp) => {
    return finalizeAuth(await authApi.verifyLoginOtp(userId, otp));
  }, []);

  const signOut = useCallback(() => {
    // Route each role back to its OWN login screen so clinics don't land
    // on the patient sign-in page (client 2026-08-28). The portal roles
    // (VENDOR/ADMIN/STAFF/SUPER_ADMIN) all authenticate via /portal-login;
    // patients (USER) use /login.
    const PORTAL_ROLES = new Set(['VENDOR', 'ADMIN', 'STAFF', 'SUPER_ADMIN']);
    const dest = PORTAL_ROLES.has(user?.role) ? '/portal-login' : '/login';
    clearAuth();
    setUser(null);
    window.location.href = dest;
  }, [user?.role]);

  const refresh = useCallback(async () => {
    const u = await authApi.me();
    setUser(u);
    setStoredUser(u);
    return u;
  }, []);

  const value = {
    user, loading,
    signIn, signUp, sendOtp, verifyOtp,
    signUpUser, signInUser, forgotPasswordOtp, resetPasswordOtp,
    signInSuperAdmin, signInPortal, verifyLoginOtp,
    signOut, refresh, setUser,
  };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
